import { createApp } from 'vue'
import App from './App.vue'
import router from './router'
import store from './store'
import i18n from './plugins/i18n'
import { CoralogixRum } from '@coralogix/browser'
import { createGtm } from '@gtm-support/vue-gtm'
import * as Sentry from '@sentry/vue'
import { plugin as unleashPlugin } from '@unleash/proxy-client-vue'
import { initSift } from '@/plugins/sift'
import { createUnleashInfo } from '@/utils/unleashInfo'
import { analyticsAllowedOnHost } from '@/utils/analyticsHost'

// Initialize Sift (anonymous or later with userId on login)
initSift()
// Coralogix setup
CoralogixRum.init({
  public_key: process.env.VUE_APP_CORALOGIX_KEY,
  application: 'storefront',
  version: '2.1',
  coralogixDomain: process.env.VUE_APP_CORALOGIX_DOMAIN,
  sessionRecordingConfig: {
    enable: true,
    autoStartSessionRecording: true,
    recordConsoleEvents: true,
    sessionRecordingSampleRate: 100,
    blockClass: 'rr-block',
    ignoreClass: 'rr-ignore',
    maskTextClass: 'rr-mask',
    maskAllInputs: false,
    maskInputOptions: {
      password: true,
      text: true,
      number: true,
    },
    scale: 0.5,
    compressionQuality: 0.5,
    // The Radar store map is a maplibre-gl WebGL <canvas>, not image tiles, so
    // session replay shows an empty area where the map was. recordCanvas alone
    // defaults to serializing every WebGL call, which is expensive and does not
    // replay a maplibre scene; a numeric sampling.canvas records the canvas as an
    // image N times per second instead. Off by default: it costs GPU work on
    // every map frame plus one JPEG per second while a map is on screen, so
    // enable it per environment and verify the replay before rolling it out.
    // Do NOT swap this for inlineImages -- that sets crossOrigin="anonymous" on
    // live <img> nodes and breaks menu images served without CORS headers.
    recordCanvas: process.env.VUE_APP_CORALOGIX_RECORD_CANVAS === 'true',
    sampling: { canvas: 1 },
    dataURLOptions: { type: 'image/jpeg', quality: 0.4 },
  },
})

const app = createApp(App).use(store).use(router).use(i18n)

// Unleash feature flags
try {
  // Get initial context (chain from localStorage if available)
  const initialContext = createUnleashInfo()

  app.use(unleashPlugin, {
    config: {
      url: process.env.VUE_APP_UNLEASH_URL,
      clientKey: process.env.VUE_APP_UNLEASH_CLIENT_KEY,
      refreshInterval: 15,
      appName: process.env.VUE_APP_UNLEASH_APP_NAME,
      context: initialContext,
    },
  })
} catch (e) {
  console.warn('Unleash init skipped:', e)
}

const sentryEnabled = process.env.VUE_APP_SENTRY_ENABLED === 'true'
if (sentryEnabled) {
  Sentry.init({
    app,
    dsn: 'https://9e74111a15a1a1c35ec017d52086f4cf@o360216.ingest.sentry.io/4505833645146112',
    integrations: [
      new Sentry.BrowserTracing({
        routingInstrumentation: Sentry.vueRouterInstrumentation(router),
      }),
      new Sentry.Replay(),
    ],
    tracesSampleRate: 1.0,
    // tracePropagationTargets: [
    //   /^https:\/\/lbndprx\.lunchbox\.io/,
    //   /^https:\/\/api3\.lunchbox\.io/,
    //   /^https:\/\/.*\.novadine\.com/,
    // ],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  })
}

// Google Tag Manager
if (process.env.VUE_APP_GTM_ID && analyticsAllowedOnHost()) {
  try {
    const gtm = createGtm({
      id: process.env.VUE_APP_GTM_ID,
      enabled: true,
      debug: false,
      vueRouter: router,
    })
    if (gtm) {
      app.use(gtm)
    }
  } catch (error) {
    console.log(error)
  }
}

// Google Analytics (GA4)
const gaId = process.env.VUE_APP_GA4_ID
if (gaId && analyticsAllowedOnHost()) {
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${gaId}`
  document.head.appendChild(script)

  const configScript = document.createElement('script')
  configScript.innerHTML = `
    window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${gaId}');
  `
  document.head.appendChild(configScript)
}

// UserWay Accessibility Widget
const userwayAccountId = process.env.VUE_APP_USERWAY_ACCOUNT_ID
if (userwayAccountId) {
  const userwayScript = document.createElement('script')
  userwayScript.src = 'https://cdn.userway.org/widget.js'
  userwayScript.setAttribute('data-account', userwayAccountId)
  document.head.appendChild(userwayScript)
}

app.mount('#app')
